import clsx from 'clsx'
import { differenceInHours, differenceInMinutes, differenceInSeconds, getHours, isFriday, isWeekend, setHours, setMinutes, setSeconds } from 'date-fns'
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { useInterval } from 'react-use'

import { useTranslation } from '@Globals/Translations'

import styles from './smallShippingUrgencyTimer.module.scss'

interface WrapperProps{
  className?: string
}
const zeroTime = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }).replace(/:\d+:\d+/, ':00:00'))
const between = (x: number, from: number, to: number) => x >= from && x < to
const getTimeAtDate = (date: Date, hour: number, minute: number, second: number) => setHours(setMinutes(setSeconds(date, second), minute), hour)
const getTimeTill = (now: Date, atHour: number, atMinute: number, atSecond: number) => {
  const endAt = getTimeAtDate(now, atHour, atMinute, atSecond)
  const deltaHoursInTotal = differenceInHours(endAt, now)
  const deltaHours = deltaHoursInTotal % 24
  const deltaMinutesInTotal = differenceInMinutes(endAt, now)
  const deltaMinutes = deltaMinutesInTotal % 60
  const deltaSecondsInTotal = differenceInSeconds(endAt, now)
  const deltaSeconds = deltaSecondsInTotal % 60
  return { deltaHours, deltaMinutes, deltaSeconds }
}
const SmallShippingUrgencyTimerV2: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation()
  const [delay, setDelay] = useState(0)

  const getUrgencyTimerElement = useCallback((rightNowInDenmark = new Date((new Date()).toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }))) => {
    const weekendInDK = isWeekend(rightNowInDenmark)
    const fridayInDK = isFriday(rightNowInDenmark)
    const currentHour: number = getHours(rightNowInDenmark)
    let daytimeSaving: boolean = false
    if (rightNowInDenmark.isDSTActive) {
      daytimeSaving = rightNowInDenmark.isDSTActive()
    }
    const twoDigits = (num: number) => num.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })

    if (weekendInDK || (fridayInDK && between(currentHour, 16, 24))) {
      return <>{t('shipping:PP_DELIVERY_INFO_ORDER_NOW_MONDAY')}</>
    }
    if (between(currentHour, 0, 10)) {
      return (
        daytimeSaving
          ? t('shipping:ORDER_BEFORE_X_AND_WE_SHIP_TODAY_SUMMER_TIME')
          : t('shipping:ORDER_BEFORE_X_AND_WE_SHIP_TODAY_WINTER_TIME')
      )
    }
    if (between(currentHour, 10, 16)) {
      const { deltaHours, deltaMinutes, deltaSeconds } = getTimeTill(rightNowInDenmark, 16, 0, 0)
      return (
        <>
          {`${t('shipping:PP_DELIVERY_INFO_ORDER_WITHIN')} `}
          <strong>
            <strong className={styles.styledTimerNumber}>{twoDigits(deltaHours)}</strong>
            {` ${t('cms:HOURS_LABEL')} `}
            <strong className={styles.styledTimerNumber}>{twoDigits(deltaMinutes)}</strong>
            {` ${t('cms:MINUTES_LABEL')} `}
            <strong className={styles.styledTimerNumber}>{twoDigits(deltaSeconds)}</strong>
            {` ${t('cms:SECONDS_LABEL')}`}
          </strong>
          {` ${t('shipping:PP_DELIVERY_INFO_AND_WE_SHIP')}`}
          {` ${t('shipping:PP_DELIVERY_INFO_TODAY')}`}
        </>
      )
    }
    if (between(currentHour, 16, 18)) {
      return <>{t('shipping:PP_DELIVERY_INFO_ORDER_NOW_TOMORROW')}</>
    }
    if (between(currentHour, 18, 24)) {
      const { deltaHours, deltaMinutes, deltaSeconds } = getTimeTill(rightNowInDenmark, 24, 0, 0)
      return (
        <>
          {`${t('shipping:PP_DELIVERY_INFO_ORDER_WITHIN')} `}
          <strong>
            <strong className={styles.styledTimerNumber}>{twoDigits(deltaHours)}</strong>
            {` ${t('cms:HOURS_LABEL')} `}
            <strong className={styles.styledTimerNumber}>{twoDigits(deltaMinutes)}</strong>
            {` ${t('cms:MINUTES_LABEL')} `}
            <strong className={styles.styledTimerNumber}>{twoDigits(deltaSeconds)}</strong>
            {` ${t('cms:SECONDS_LABEL')}`}
          </strong>
          {` ${t('shipping:PP_DELIVERY_INFO_AND_WE_SHIP')}`}
          {` ${t('shipping:PP_DELIVERY_INFO_TOMORROW_URGENCY_TIMER')}`}
        </>
      )
    }
    return <>{t('shipping:PP_DELIVERY_INFO_ORDER_NOW_MONDAY')}</>
  }, [t])

  const [childElement, setChildElement] = useState<ReactNode>(getUrgencyTimerElement(zeroTime()))

  useInterval(() => setChildElement(getUrgencyTimerElement()), delay)
  useEffect(() => {
    setDelay(1000)
  }, [])

  return (
    <div className={clsx(styles.smallUrgencyTimer, className)} suppressHydrationWarning>
      {childElement}
    </div>
  )
}

export const SmallShippingUrgencyTimer: FC<WrapperProps> = ({ className }) => (
  <SmallShippingUrgencyTimerV2
    className={className}
  />
)
